// ============================================================================
// TREE VISUALIZER  v2
//
// Renders BST, AVL, Red-Black, Segment Tree, and Heap data structures.
//
// Layout algorithms
// ─────────────────
//   TREE PAYLOAD  → Reingold-Tilford (RT) algorithm.
//     Phase 1: post-order contour computation assigns a preliminary x for every
//              node so that no two subtrees overlap, with a configurable minimum
//              horizontal separation (NODE_SEP).
//     Phase 2: pre-order offset propagation adds each node's accumulated
//              modifier to produce final x coordinates.
//     Phase 3: translate so the root sits at the horizontal centre of the
//              viewport and the whole tree is vertically centred.
//
//   ARRAY PAYLOAD → binary-heap index geometry.
//     Node i (0-based) lives at level ⌊log₂(i+1)⌋.
//     Its position within the level is i − (2^level − 1).
//     The horizontal slot width at each level is W / 2^level so deeper
//     levels pack tighter but never overlap.
//
// Node state precedence
// ─────────────────────
//   active  > visited  > compare  > heapified  > sorted  > default
//
// Special node rings
// ──────────────────
//   AVL balance factor: when node.height is present, a small numeric badge
//   outside the ring shows the balance factor (right_height - left_height).
//
//   Red-Black colour ring: when node.color === 'red' | 'black', a 3px outer
//   ring in the matching semantic colour is drawn around the node.
//
// Edge rendering
// ──────────────
//   Edges are trimmed to node circle boundaries.  Edges from the active
//   (current) node inherit the amber active stroke.  All other edges are
//   dim slate with 0.45 opacity.
//
// Focal glow pass
// ───────────────
//   Active (current) nodes get an amber halo ring + SVG blur glow.
//   Drawn in a final pass above all node fills so it is always visible.
//
// Container measurement
// ──────────────────────
//   ResizeObserver drives a (W, H) state update.  The SVG always exactly
//   fills its parent container — no hardcoded width/height props needed.
// ============================================================================

import React, { useMemo, useRef, useEffect, useState, memo } from 'react';
import type {
  ExecutionStep,
  ArrayStatePayload,
  TreeStatePayload,
  TreeNode as PayloadTreeNode,
} from '../../../core/types/rust-engine-interfaces';
import { StepType } from '../../../core/types/rust-engine-interfaces';
import { useAccessibility } from '../../../core/context/AccessibilityContext';

// ── Props ─────────────────────────────────────────────────────────────────────

interface TreeVisualizerProps {
  currentStep: ExecutionStep | null;
  algorithmId?: string;
}

// ── Geometry constants ────────────────────────────────────────────────────────

const R        = 24;    // node radius
const LEVEL_H  = 80;   // vertical spacing between levels
const NODE_SEP = 18;   // minimum horizontal gap between node circles (RT)
const PAD_X    = 48;   // horizontal canvas padding
const PAD_TOP  = 56;   // vertical padding above root

// ── Color tokens ──────────────────────────────────────────────────────────────

const C = {
  bg:           'transparent',
  edgeDefault:  '#1e293b',
  edgeActive:   '#f59e0b',
  edgePath:     '#22c55e',
  edgeCompare:  '#818cf8',

  fillDefault:  'rgba(10, 18, 32, 0.95)',
  fillVisited:  'rgba(3, 24, 16, 0.95)',
  fillActive:   'rgba(42, 30, 0, 0.95)',
  fillCompare:  'rgba(20, 10, 50, 0.95)',
  fillHeap:     'rgba(3, 30, 35, 0.95)',
  fillSorted:   'rgba(3, 30, 16, 0.95)',

  strokeDefault:  '#1e3348',
  strokeVisited:  '#22c55e',
  strokeActive:   '#f59e0b',
  strokeCompare:  '#818cf8',
  strokeHeap:     '#22d3ee',
  strokeSorted:   '#4ade80',

  labelDefault:   '#7d95b0',
  labelVisited:   '#4ade80',
  labelActive:    '#fde68a',
  labelCompare:   '#c7d2fe',
  labelHeap:      '#67e8f9',

  rbRed:    '#ef4444',
  rbBlack:  '#1e293b',
  avlBadge: '#60a5fa',
  glow:     '#f59e0b',

  treapPri:     '#f472b6',
  leftistNpl:   '#a78bfa',
  binomialDeg:  '#22d3ee',
  btreeKey:     '#67e8f9',
  btreeSplit:   '#fbbf24',
  splayLabel:   '#fbbf24',
  heapSwap:     '#f59e0b',
  cartesianIdx: '#94a3b8',
  pairingLabel: '#34d399',
  violationRed: '#ef4444',
  trieEnd:      '#22c55e',
  trieEdge:     '#3b82f6',
  liftLabel:    '#06b6d4',
  liftPath:     '#f97316',
  pstVersion:   '#a855f7',
  ropeLeaf:     '#f59e0b',
  ropeInternal: '#6366f1',
};

// ── Node state ────────────────────────────────────────────────────────────────

type NodeState = 'default' | 'visited' | 'active' | 'compare' | 'heapified' | 'sorted' | 'highlight';

// ── Color-blind shape overlays ──────────────────────────────────────────────

function CbShapeOverlay({ cx, cy, state }: { cx: number; cy: number; state: NodeState }) {
  const s = R * 0.45;
  switch (state) {
    case 'active':    // diamond
      return <polygon points={`${cx},${cy-s} ${cx+s},${cy} ${cx},${cy+s} ${cx-s},${cy}`} fill="none" stroke="rgba(255,255,255,0.65)" strokeWidth={1.5} aria-hidden="true" />;
    case 'visited':   // hollow dot
      return <circle cx={cx} cy={cy} r={s * 0.65} fill="none" stroke="rgba(255,255,255,0.65)" strokeWidth={1.5} aria-hidden="true" />;
    case 'compare':   // triangle
      return <polygon points={`${cx},${cy-s} ${cx+s},${cy+s*0.6} ${cx-s},${cy+s*0.6}`} fill="none" stroke="rgba(255,255,255,0.65)" strokeWidth={1.5} aria-hidden="true" />;
    case 'heapified': // plus
      return (
        <g aria-hidden="true" stroke="rgba(255,255,255,0.65)" strokeWidth={1.5} fill="none">
          <line x1={cx} y1={cy-s} x2={cx} y2={cy+s} />
          <line x1={cx-s} y1={cy} x2={cx+s} y2={cy} />
        </g>
      );
    case 'highlight': // circle
      return <circle cx={cx} cy={cy} r={s * 0.75} fill="none" stroke="rgba(255,255,255,0.65)" strokeWidth={1.5} aria-hidden="true" />;
    case 'sorted':    // hollow square
      return <rect x={cx-s*0.55} y={cy-s*0.55} width={s*1.1} height={s*1.1} fill="none" stroke="rgba(255,255,255,0.65)" strokeWidth={1.5} aria-hidden="true" />;
    default:
      return null;
  }
}

interface PlacedNode {
  id: string;
  value: string | number;
  label?: string;
  x: number;
  y: number;
  level: number;
  state: NodeState;
  leftChildId?:  string;
  rightChildId?: string;
  childrenIds?:  string[];
  leftArrIdx?:   number;
  rightArrIdx?:  number;
  rbColor?: 'red' | 'black';
  height?: number;
  // Algorithm-specific metadata (parsed from value or metadata)
  priority?: number;       // Treap
  npl?: number;            // Leftist Heap (null path length / dist)
  degree?: number;         // Binomial Heap
  multiKeys?: number[];    // B-tree / B+ tree
  inorderIndex?: number;   // Cartesian Tree
  axis?: number;           // KD-Tree (0=x, 1=y)
  signature?: TreeSignature; // Detected algorithm type
}

// ── Algorithm detection ──────────────────────────────────────────────────────

type TreeSignature =
  | 'avl' | 'rb' | 'splay' | 'treap' | 'cartesian'
  | 'heap' | 'binomial' | 'pairing' | 'leftist'
  | 'btree' | 'bplus' | 'kd' | 'trie' | 'binary-lift'
  | 'persistent-segtree' | 'rope' | 'range-tree'
  | 'huffman' | 'minimax' | 'bst' | 'generic';

function detectTreeSignature(
  explanation: string,
  nodes: PayloadTreeNode[],
  hasBalanceFactors: boolean,
  hasColors: boolean,
  algorithmId?: string,
): TreeSignature {
  const txt = explanation.toLowerCase();
  const aid = (algorithmId ?? '').toLowerCase();
  // AlgorithmId-based detection (highest priority)
  if (aid.includes('bst') || aid.includes('binary-search-tree')) return 'bst';
  if (aid.includes('trie') || aid.includes('prefix-tree')) return 'trie';
  if (aid.includes('binary-lift') || aid.includes('lca')) return 'binary-lift';
  if (aid.includes('persistent-segment')) return 'persistent-segtree';
  if (aid.includes('rope')) return 'rope';
  if (aid.includes('range-tree') || aid.includes('rangetree')) return 'range-tree';
  if (aid.includes('huffman')) return 'huffman';
  if (aid.includes('minimax')) return 'minimax';
  if (aid.includes('avl')) return 'avl';
  if (aid.includes('red-black') || aid.includes('rb-tree')) return 'rb';
  if (aid.includes('splay')) return 'splay';
  if (aid.includes('treap')) return 'treap';
  if (aid.includes('cartesian')) return 'cartesian';
  if (aid.includes('binomial')) return 'binomial';
  if (aid.includes('pairing')) return 'pairing';
  if (aid.includes('leftist')) return 'leftist';
  if (aid.includes('b-plus') || aid.includes('b+tree') || aid.includes('bplus')) return 'bplus';
  if (aid.includes('b-tree') || aid.includes('btree')) return 'btree';
  if (aid.includes('kd-tree') || aid.includes('kdtree')) return 'kd';
  // Explanation-based detection (fallback)
  if (hasBalanceFactors || txt.includes('avl')) return 'avl';
  if (hasColors || txt.includes('red-black')) return 'rb';
  if (txt.includes('splay') || txt.includes('zig')) return 'splay';
  if (txt.includes('treap') || txt.includes('heap violation')) return 'treap';
  if (txt.includes('cartesian')) return 'cartesian';
  if (txt.includes('binomial')) return 'binomial';
  if (txt.includes('pairing')) return 'pairing';
  if (txt.includes('leftist')) return 'leftist';
  if (txt.includes('b-tree') || txt.includes('b tree')) return 'btree';
  if (txt.includes('b+ tree') || txt.includes('b+tree')) return 'bplus';
  if (txt.includes('kd-tree') || txt.includes('kd tree')) return 'kd';
  if (txt.includes('trie') || txt.includes('prefix tree')) return 'trie';
  if (txt.includes('binary lift') || txt.includes('lca') || txt.includes('ancestor')) return 'binary-lift';
  if (txt.includes('persistent')) return 'persistent-segtree';
  if (txt.includes('rope')) return 'rope';
  if (txt.includes('range tree') || txt.includes('range-tree')) return 'range-tree';
  if (txt.includes('huffman')) return 'huffman';
  if (txt.includes('minimax') || txt.includes('game tree')) return 'minimax';
  // Detect from node value patterns
  for (const n of nodes) {
    const v = String(n.value);
    if (v.includes('(p:')) return 'treap';
    if (v.includes('(d:')) return 'leftist';
    if (/\(B\d+\)/.test(v)) return 'binomial';
    if (v.includes('|') && /^\d+(\|\d+)+$/.test(v)) return 'btree';
    if (v.includes('/x') || v.includes('/y')) return 'kd';
  }
  return 'generic';
}

// Parse priority from treap value "key(p:0.42)"
function parsePriority(val: string): number | undefined {
  const m = val.match(/\(p:([\d.]+)\)/);
  return m?.[1] ? parseFloat(m[1]) : undefined;
}

// Parse npl/dist from leftist value "key(d:3)"
function parseNpl(val: string): number | undefined {
  const m = val.match(/\(d:(\d+)\)/);
  return m?.[1] ? parseInt(m[1], 10) : undefined;
}

// Parse degree from binomial value "key(B2)"
function parseDegree(val: string): number | undefined {
  const m = val.match(/\(B(\d+)\)/);
  return m?.[1] ? parseInt(m[1], 10) : undefined;
}

// Parse multi-keys from B-tree value "10|20|30"
function parseMultiKeys(val: string): number[] | undefined {
  if (!val.includes('|')) return undefined;
  const parts = val.split('|').map(s => parseInt(s.trim(), 10));
  if (parts.every(p => !isNaN(p))) return parts;
  return undefined;
}

// Parse KD-tree axis from value "(x,y)/x" or "(x,y)/y"
function parseAxis(val: string): number | undefined {
  if (val.endsWith('/x')) return 0;
  if (val.endsWith('/y')) return 1;
  return undefined;
}

// Detect splay step type from explanation
function parseSplayStep(explanation: string): 'zig' | 'zig-zig' | 'zig-zag' | null {
  const txt = explanation.toLowerCase();
  if (txt.includes('zig-zig')) return 'zig-zig';
  if (txt.includes('zig-zag')) return 'zig-zag';
  if (txt.includes('zig')) return 'zig';
  return null;
}

// Detect rotation type from explanation
function parseRotation(explanation: string): 'LL' | 'RR' | 'LR' | 'RL' | null {
  const txt = explanation.toLowerCase();
  if (txt.includes('left-right') || txt.includes('lr rotation')) return 'LR';
  if (txt.includes('right-left') || txt.includes('rl rotation')) return 'RL';
  if (txt.includes('left rotation') && txt.includes('right rotation')) {
    // Could be LR or RL — check order
    if (txt.indexOf('left rotation') < txt.indexOf('right rotation')) return 'LR';
    return 'RL';
  }
  if (txt.includes('double right') || txt.includes('ll')) return 'LL';
  if (txt.includes('double left') || txt.includes('rr')) return 'RR';
  if (txt.includes('right rotation')) return 'LL';
  if (txt.includes('left rotation')) return 'RR';
  return null;
}

// Detect heap action from explanation
function parseHeapAction(explanation: string): 'sift-up' | 'sift-down' | 'swap' | null {
  const txt = explanation.toLowerCase();
  if (txt.includes('sift-up') || txt.includes('sift up') || txt.includes('bubble up')) return 'sift-up';
  if (txt.includes('sift-down') || txt.includes('sift down') || txt.includes('sink down')) return 'sift-down';
  if (txt.includes('swap')) return 'swap';
  return null;
}

// ── Node style lookup ─────────────────────────────────────────────────────────

function nodeStyle(state: NodeState) {
  switch (state) {
    case 'active':    return { fill: C.fillActive,   stroke: C.strokeActive,   label: C.labelActive   };
    case 'visited':   return { fill: C.fillVisited,  stroke: C.strokeVisited,  label: C.labelVisited  };
    case 'compare':   return { fill: C.fillCompare,  stroke: C.strokeCompare,  label: C.labelCompare  };
    case 'heapified': return { fill: C.fillHeap,     stroke: C.strokeHeap,     label: C.labelHeap     };
    case 'highlight': return { fill: '#3b0764',      stroke: '#a855f7',        label: '#d8b4fe'       };
    case 'sorted':    return { fill: C.fillSorted,   stroke: C.strokeSorted,   label: C.labelVisited  };
    default:          return { fill: C.fillDefault,  stroke: C.strokeDefault,  label: C.labelDefault  };
  }
}

// ── Reingold-Tilford layout ───────────────────────────────────────────────────
//
// Implementation of the Reingold-Tilford "tidy trees" algorithm.
// Works on the PayloadTreeNode map; produces a Map<id, {x, y}>.

interface RTNode {
  id: string;
  node: PayloadTreeNode;
  x: number;
  mod: number;     // accumulated shift for right subtree offset
  level: number;
}

function buildRTMap(
  nodeMap: Map<string, PayloadTreeNode>,
): Map<string, RTNode> {
  const rt = new Map<string, RTNode>();
  nodeMap.forEach((n) => rt.set(n.id, { id: n.id, node: n, x: 0, mod: 0, level: 0 }));
  return rt;
}

function getChildren(n: PayloadTreeNode): string[] {
  if (n.left && n.right) return [n.left, n.right];
  if (n.left) return [n.left];
  if (n.right) return [n.right];
  if (n.children && n.children.length > 0) return n.children;
  return [];
}

function rtFirstPass(
  id: string,
  depth: number,
  nodeMap: Map<string, PayloadTreeNode>,
  rt: Map<string, RTNode>,
  contour: Map<number, number[]>, // level → [rightmost x per subtree]
): void {
  const rtn = rt.get(id);
  if (!rtn) return;
  rtn.level = depth;

  const n = rtn.node;
  const childIds = getChildren(n);

  // Recurse into all children
  childIds.forEach(cid => rtFirstPass(cid, depth + 1, nodeMap, rt, contour));

  if (childIds.length === 0) {
    rtn.x = 0;
    return;
  }
  if (childIds.length === 1) {
    rtn.x = rt.get(childIds[0]!)!.x;
    return;
  }

  // Multiple children — space them out left to right
  const minGap = R * 2 + NODE_SEP;

  // Helper to get contour of a subtree (evaluates actual x coordinates at current mods)
  const getContour = (rootId: string, isLeft: boolean) => {
    const res = new Map<number, number>();
    const traverse = (nodeId: string, modSum: number) => {
      const rtn = rt.get(nodeId)!;
      const absX = rtn.x + modSum;
      const d = rtn.level;
      if (!res.has(d)) res.set(d, absX);
      else {
        if (isLeft) res.set(d, Math.min(res.get(d)!, absX));
        else res.set(d, Math.max(res.get(d)!, absX));
      }
      const cIds = getChildren(rtn.node);
      cIds.forEach(cid => traverse(cid, modSum + rtn.mod));
    };
    traverse(rootId, 0);
    return res;
  };

  for (let i = 1; i < childIds.length; i++) {
    // rightmost contour of all siblings to the left (0..i-1)
    const leftRightContour = new Map<number, number>();
    for (let j = 0; j < i; j++) {
      const rc = getContour(childIds[j]!, false);
      rc.forEach((val, d) => {
        if (!leftRightContour.has(d)) leftRightContour.set(d, val);
        else leftRightContour.set(d, Math.max(leftRightContour.get(d)!, val));
      });
    }

    // leftmost contour of the current subtree
    const childRT = rt.get(childIds[i]!)!;
    const rightLeftContour = getContour(childIds[i]!, true);
    
    let maxShift = minGap - (childRT.x - rt.get(childIds[i - 1]!)!.x); // base gap between roots
    if (maxShift < 0) maxShift = 0;

    rightLeftContour.forEach((leftX, d) => {
      if (leftRightContour.has(d)) {
        const rightX = leftRightContour.get(d)!;
        const shift = (rightX + minGap) - leftX;
        if (shift > maxShift) maxShift = shift;
      }
    });

    if (maxShift > 0) {
      childRT.mod += maxShift;
      childRT.x += maxShift;
    }
  }

  // Parent x is midpoint of leftmost and rightmost child
  const lx = rt.get(childIds[0]!)!.x;
  const rx = rt.get(childIds[childIds.length - 1]!)!.x;
  rtn.x = (lx + rx) / 2;
}

function rtSecondPass(
  id: string,
  modSum: number,
  nodeMap: Map<string, PayloadTreeNode>,
  rt: Map<string, RTNode>,
  finalX: Map<string, number>,
): void {
  const rtn = rt.get(id);
  if (!rtn) return;
  finalX.set(id, rtn.x + modSum);
  const childIds = getChildren(rtn.node);
  childIds.forEach(cid => rtSecondPass(cid, modSum + rtn.mod, nodeMap, rt, finalX));
}

function computeRTLayout(
  nodes: PayloadTreeNode[],
  W: number,
  H: number,
): Map<string, { x: number; y: number; level: number }> {
  const result  = new Map<string, { x: number; y: number; level: number }>();
  if (nodes.length === 0) return result;

  const nodeMap = new Map<string, PayloadTreeNode>();
  nodes.forEach((n) => nodeMap.set(n.id, n));

  const root = nodes.find((n) => !n.parent) ?? nodes[0]!;

  // Phase 1: assign preliminary x and mod
  const rt = buildRTMap(nodeMap);
  rtFirstPass(root.id, 0, nodeMap, rt, new Map());

  // Phase 2: propagate mods to get final x, y
  const finalX = new Map<string, number>();
  rtSecondPass(root.id, 0, nodeMap, rt, finalX);

  // Compute level depths
  const levelOf = new Map<string, number>();
  const maxLevel = { v: 0 };
  const assignLevel = (id: string, d: number) => {
    levelOf.set(id, d);
    if (d > maxLevel.v) maxLevel.v = d;
    const n = nodeMap.get(id);
    if (n) {
      const childIds = getChildren(n);
      childIds.forEach(cid => assignLevel(cid, d + 1));
    }
  };
  assignLevel(root.id, 0);

  // Find x range to centre the tree
  let minX = Infinity;
  let maxX = -Infinity;
  finalX.forEach((x) => { if (x < minX) minX = x; if (x > maxX) maxX = x; });
  const treeW = maxX - minX;
  const offsetX = (W - treeW) / 2 - minX;

  // Scale vertical spacing to fit H
  const levels   = maxLevel.v + 1;
  const availH   = H - PAD_TOP - 32;
  const yStep    = levels > 1 ? Math.min(LEVEL_H, availH / (levels - 1)) : LEVEL_H;

  finalX.forEach((x, id) => {
    const lv = levelOf.get(id) ?? 0;
    result.set(id, { x: x + offsetX, y: PAD_TOP + lv * yStep, level: lv });
  });

  return result;
}

// ── Heap array layout ─────────────────────────────────────────────────────────
//
// For node at 0-based index i:
//   level = ⌊log₂(i+1)⌋
//   posInLevel = i − (2^level − 1)
//   slotWidth  = W / 2^level
//   x = slotWidth * posInLevel + slotWidth / 2

function computeHeapLayout(
  count: number,
  W: number,
  H: number,
): Map<number, { x: number; y: number; level: number }> {
  const result = new Map<number, { x: number; y: number; level: number }>();
  if (count === 0) return result;

  const levels = Math.floor(Math.log2(count)) + 1;
  const availH = H - PAD_TOP - 32;
  const yStep  = levels > 1 ? Math.min(LEVEL_H, availH / (levels - 1)) : LEVEL_H;
  const usableW = W - PAD_X * 2;

  for (let i = 0; i < count; i++) {
    const level       = Math.floor(Math.log2(i + 1));
    const posInLevel  = i - ((1 << level) - 1);
    const slotsInLevel = 1 << level;
    const slotW       = usableW / slotsInLevel;
    const x           = PAD_X + slotW * posInLevel + slotW / 2;
    const y           = PAD_TOP + level * yStep;
    result.set(i, { x, y, level });
  }

  return result;
}

// ── Edge trimming ─────────────────────────────────────────────────────────────

function trimEdge(
  x1: number, y1: number,
  x2: number, y2: number,
  toRadius: number = R,
  fromRadius: number = R,
): { x1: number; y1: number; x2: number; y2: number } {
  const dx  = x2 - x1;
  const dy  = y2 - y1;
  const len = Math.hypot(dx, dy);
  if (len < (fromRadius + toRadius) + 0.001) return { x1, y1, x2, y2 };
  const ux = dx / len;
  const uy = dy / len;
  return {
    x1: x1 + ux * fromRadius,
    y1: y1 + uy * fromRadius,
    x2: x2 - ux * toRadius,
    y2: y2 - uy * toRadius,
  };
}

// ── Component ─────────────────────────────────────────────────────────────────

function treeStructureKey(nodes: PayloadTreeNode[]): string {
  const parts = nodes.map(n => {
    const kids = n.children?.length ? n.children.join(',') : `${n.left ?? ''}|${n.right ?? ''}`;
    return `${n.id}:${n.parent ?? ''}:${kids}`;
  });
  return parts.sort().join(';');
}

export const TreeVisualizer: React.FC<TreeVisualizerProps> = memo(({ currentStep, algorithmId = '' }) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [size, setSize] = useState({ w: 700, h: 460 });

  // ── Stable layout cache ───────────────────────────────────────────────────
  // Keyed on tree structure (node IDs + parent-child edges) and viewport size.
  // Prevents layout recomputation when only node states change between steps.
  const stableTreeLayoutRef = useRef<Map<string, { x: number; y: number; level: number }>>(new Map());
  const stableTreeKeyRef     = useRef<string>('');
  const stableTreeSizeRef    = useRef<{ w: number; h: number }>({ w: 0, h: 0 });
  const stableHeapLayoutRef  = useRef<Map<number, { x: number; y: number; level: number }>>(new Map());
  const stableHeapCountRef   = useRef<number>(-1);
  const stableHeapSizeRef    = useRef<{ w: number; h: number }>({ w: 0, h: 0 });

  // ── Container measurement ─────────────────────────────────────────────────

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const ro = new ResizeObserver((entries) => {
      const entry = entries[0];
      if (!entry) return;
      const { width, height } = entry.contentRect;
      setSize({ w: Math.max(300, Math.round(width)), h: Math.max(200, Math.round(height)) });
    });
    ro.observe(el);
    const rect = el.getBoundingClientRect();
    setSize({ w: Math.max(300, Math.round(rect.width)), h: Math.max(200, Math.round(rect.height)) });
    return () => ro.disconnect();
  }, []);

  const { w: W, h: H } = size;

  // ── Placed nodes ──────────────────────────────────────────────────────────

  const placed = useMemo((): PlacedNode[] => {
    try {
      // ── Tree payload ───────────────────────────────────────────────────
      if (currentStep?.payload?.type === 'tree') {
        const payload    = currentStep.payload as TreeStatePayload;
        const rawNodes   = payload.nodes ?? [];
        if (rawNodes.length === 0) return [];

        const currentId  = payload.metadata?.currentNode;
        const visitedSet = new Set(payload.metadata?.visitedNodes ?? []);
        const explanation = currentStep.metadata?.explanation ?? '';
        const hasColors = rawNodes.some(n => n.color !== undefined);
        const hasBF = payload.metadata?.balanceFactors !== undefined;
        const signature = detectTreeSignature(explanation, rawNodes, hasBF, hasColors, algorithmId);
        const balanceFactors = payload.metadata?.balanceFactors as Record<string, number> | undefined;

        const structKey = treeStructureKey(rawNodes);
        if (structKey === stableTreeKeyRef.current
            && stableTreeLayoutRef.current.size > 0
            && stableTreeSizeRef.current.w === W
            && stableTreeSizeRef.current.h === H) {
          // Reuse cached layout — structure and viewport unchanged
        } else {
          stableTreeLayoutRef.current = computeRTLayout(rawNodes, W, H);
          stableTreeKeyRef.current    = structKey;
          stableTreeSizeRef.current   = { w: W, h: H };
        }
        const layout = stableTreeLayoutRef.current;

        // Detect forest dummy root (binomial heap creates id='forest', value='heap')
        const forestRootId = rawNodes.find(n =>
          n.id === 'forest' && (n.value === 'heap' || n.value === 'Forest')
        )?.id;

        // Assign inorder indices for Cartesian tree (based on value order)
        let inorderCounter = 0;
        const inorderMap = new Map<string, number>();
        if (signature === 'cartesian') {
          const sorted = [...rawNodes].sort((a, b) => {
            const va = typeof a.value === 'number' ? a.value : parseFloat(String(a.value));
            const vb = typeof b.value === 'number' ? b.value : parseFloat(String(b.value));
            return va - vb;
          });
          sorted.forEach(n => inorderMap.set(n.id, inorderCounter++));
        }

        return rawNodes.map((n): PlacedNode | null => {
          const pos = layout.get(n.id);
          if (!pos) return null;

          let state: NodeState = 'default';
          if (n.id === currentId)     state = 'active';
          else if (visitedSet.has(n.id)) state = 'visited';

          const valStr = String(n.value);
          const pn: PlacedNode = {
            id: n.id,
            value: n.value,
            label: n.label,
            x: pos.x,
            y: pos.y,
            level: pos.level,
            state,
            leftChildId:  n.left,
            rightChildId: n.right,
            childrenIds:  n.children,
            rbColor: n.color,
            height: n.height,
            signature,
          };

          // Parse algorithm-specific metadata from node value
          if (signature === 'treap') {
            pn.priority = parsePriority(valStr);
          } else if (signature === 'leftist') {
            pn.npl = parseNpl(valStr);
          } else if (signature === 'binomial') {
            pn.degree = parseDegree(valStr);
          } else if (signature === 'btree' || signature === 'bplus') {
            pn.multiKeys = parseMultiKeys(valStr);
          } else if (signature === 'kd') {
            pn.axis = parseAxis(valStr);
          } else if (signature === 'cartesian') {
            pn.inorderIndex = inorderMap.get(n.id);
          }

          // AVL balance factor from metadata
          if (signature === 'avl' && balanceFactors) {
            pn.height = balanceFactors[n.id];
          }

          return pn;
        }).filter((n): n is PlacedNode => n !== null && n.id !== forestRootId);
      }

      // ── Array payload (heap) ───────────────────────────────────────────
      if (currentStep?.payload?.type === 'array') {
        const payload = currentStep.payload as ArrayStatePayload;
        const items   = payload.items ?? [];
        if (items.length === 0) return [];

        const meta          = payload.metadata;
        const currentIdx    = meta?.currentIndex    ?? -1;
        const compareIdxs   = new Set(meta?.compareIndices ?? (meta?.compareIndex !== undefined ? [meta.compareIndex] : []));
        const highlightIdxs = new Set(meta?.highlightIndices ?? []);
        const heapIdxs      = new Set(meta?.heapifiedIndices ?? []);
        const sortedIdxs    = new Set(meta?.sortedIndices ?? []);

        if (items.length === stableHeapCountRef.current
            && stableHeapLayoutRef.current.size > 0
            && stableHeapSizeRef.current.w === W
            && stableHeapSizeRef.current.h === H) {
          // Reuse cached heap layout — count and viewport unchanged
        } else {
          stableHeapLayoutRef.current = computeHeapLayout(items.length, W, H);
          stableHeapCountRef.current  = items.length;
          stableHeapSizeRef.current   = { w: W, h: H };
        }
        const layout = stableHeapLayoutRef.current;

        return items.map((item, i): PlacedNode | null => {
          const pos = layout.get(i);
          if (!pos) return null;

          let state: NodeState = 'default';
          if (i === currentIdx)       state = 'active';
          else if (compareIdxs.has(i))  state = 'compare';
          else if (highlightIdxs.has(i)) state = 'highlight';
          else if (heapIdxs.has(i))    state = 'heapified';
          else if (sortedIdxs.has(i))  state = 'sorted';

          return {
            id: `node-${i}`,
            value: item.value,
            x: pos.x,
            y: pos.y,
            level: pos.level,
            state,
            leftArrIdx:  2 * i + 1 < items.length ? 2 * i + 1 : undefined,
            rightArrIdx: 2 * i + 2 < items.length ? 2 * i + 2 : undefined,
          };
        }).filter((n): n is PlacedNode => n !== null);
      }
    } catch (err) {
      console.error('[TreeVisualizer] layout error:', err);
    }
    return [];
  }, [currentStep, W, H]);

  // ── Empty state ───────────────────────────────────────────────────────────

  if (placed.length === 0) {
    return (
      <div ref={containerRef} className="flex items-center justify-center w-full h-full text-slate-500">
        <div className="text-center space-y-1">
          <p className="text-sm font-medium">No tree data</p>
          <p className="text-xs">Execute a tree or heap algorithm to begin</p>
        </div>
      </div>
    );
  }

  // ── Build node index map ──────────────────────────────────────────────────

  const nodeById  = new Map<string, PlacedNode>(placed.map((n) => [n.id, n]));
  const nodeByIdx = new Map<number, PlacedNode>();
  placed.forEach((n) => {
    if (n.leftArrIdx !== undefined || n.rightArrIdx !== undefined) {
      const idx = parseInt(n.id.replace('node-', ''), 10);
      if (!isNaN(idx)) nodeByIdx.set(idx, n);
    }
  });

  // ── Edge elements ─────────────────────────────────────────────────────────

  const edgeElements: React.ReactElement[] = [];
  placed.forEach((n) => {
    const children: PlacedNode[] = [];
    if (n.leftChildId)  { const c = nodeById.get(n.leftChildId);  if (c) children.push(c); }
    if (n.rightChildId) { const c = nodeById.get(n.rightChildId); if (c) children.push(c); }
    if (n.childrenIds)  { n.childrenIds.forEach(cid => { const c = nodeById.get(cid); if (c) children.push(c); }); }
    if (n.leftArrIdx  !== undefined) { const c = nodeByIdx.get(n.leftArrIdx);  if (c) children.push(c); }
    if (n.rightArrIdx !== undefined) { const c = nodeByIdx.get(n.rightArrIdx); if (c) children.push(c); }

    children.forEach((child, ci) => {
      const isActive  = n.state === 'active'  || child.state === 'active';
      const isCompare = n.state === 'compare' || child.state === 'compare';
      const isVisited = n.state === 'visited' && child.state === 'visited';
      let stroke   = C.edgeDefault;
      let sw       = 1.5;
      let opacity  = 0.45;
      let markerId = 'tv-arr-default';
      if (isActive)  { stroke = C.edgeActive;  sw = 2.5; opacity = 1;    markerId = 'tv-arr-active';  }
      else if (isCompare) { stroke = C.edgeCompare; sw = 2; opacity = 0.85; markerId = 'tv-arr-compare'; }
      else if (isVisited) { stroke = C.edgePath;    sw = 2; opacity = 0.75; markerId = 'tv-arr-path';   }
      // arrowClearance=6 keeps tip on node boundary
      const isTree = n.leftChildId !== undefined || n.rightChildId !== undefined || (n.childrenIds && n.childrenIds.length > 0);
      const t = trimEdge(n.x, n.y, child.x, child.y, isTree ? R + 6 : R);
      edgeElements.push(
        <line
          key={`edge-${n.id}-${ci}`}
          x1={t.x1} y1={t.y1} x2={t.x2} y2={t.y2}
          stroke={stroke}
          strokeWidth={sw}
          strokeLinecap="round"
          opacity={opacity}
          markerEnd={isTree ? `url(#${markerId})` : undefined}
        />,
      );

      // Trie: label edge with the child character
      const allSignature = placed[0]?.signature;
      if (allSignature === 'trie') {
        const childLabel = String(child.label ?? child.value ?? '').replace(/\*$/, '');
        if (childLabel && childLabel !== 'root') {
          const midX = (t.x1 + t.x2) / 2;
          const midY = (t.y1 + t.y2) / 2;
          edgeElements.push(
            <text
              key={`edge-label-${n.id}-${ci}`}
              x={midX + 6} y={midY}
              fill={C.trieEdge}
              fontSize="8" fontWeight="bold"
              textAnchor="middle" dominantBaseline="middle"
              style={{ userSelect: 'none' }}
            >
              {childLabel}
            </text>,
          );
        }
      }
    });
  });

  // ── Node elements ─────────────────────────────────────────────────────────

  const { colorBlindMode, reducedMotion } = useAccessibility();
  const explanation = currentStep?.metadata?.explanation ?? '';
  const stepType = currentStep?.stepType;
  const splayStep = parseSplayStep(explanation);
  const rotationType = parseRotation(explanation);
  const heapAction = parseHeapAction(explanation);
  const isViolation = stepType === StepType.Decision && (explanation.toLowerCase().includes('violation') || explanation.toLowerCase().includes('swap'));
  const isSplit = stepType === StepType.Decision && explanation.toLowerCase().includes('split');

  const nodeElements = placed.map((n) => {
    const st = nodeStyle(n.state);
    const nodeLabel = String(n.label ?? n.value);
    const ak = n.signature ?? 'generic';

    // ── B-tree / B+ tree: render as rounded rectangle with keys ────────
    if ((ak === 'btree' || ak === 'bplus') && n.multiKeys && n.multiKeys.length > 0) {
      const keys = n.multiKeys;
      const rectW = Math.max(R * 2, keys.length * 22 + 12);
      const rectH = R * 1.6;
      const rx = n.x - rectW / 2;
      const ry = n.y - rectH / 2;
      const isSplitNode = isSplit && n.state === 'active';
      return (
        <g key={`node-${n.id}`} role="img" aria-label={`B-tree node [${keys.join(', ')}], state: ${n.state}`}>
          <rect
            x={rx} y={ry} width={rectW} height={rectH} rx={8}
            fill={isSplitNode ? 'rgba(251,191,36,0.15)' : st.fill}
            stroke={isSplitNode ? C.btreeSplit : st.stroke}
            strokeWidth={n.state === 'active' ? 2.5 : 1.5}
            opacity={0.95}
          />
          {keys.map((k, ki) => (
            <g key={`key-${ki}`}>
              {ki > 0 && <line x1={rx + ki * 22 + 6} y1={ry + 4} x2={rx + ki * 22 + 6} y2={ry + rectH - 4} stroke={C.btreeKey} strokeWidth={0.5} opacity={0.3} />}
              <text
                x={rx + ki * 22 + 17} y={n.y}
                fill={st.label}
                fontSize="11" fontWeight="700"
                textAnchor="middle" dominantBaseline="middle"
                style={{ fontFamily: 'monospace', userSelect: 'none' }}
              >
                {k}
              </text>
            </g>
          ))}
          {isSplitNode && (
            <text x={n.x} y={ry - 6} fill={C.btreeSplit} fontSize="8" fontWeight="bold" textAnchor="middle">
              SPLIT
            </text>
          )}
        </g>
      );
    }

    // ── All other algorithms: render as circle ─────────────────────────
    const showBf = ak === 'avl' && n.height !== undefined;
    const bfLabel = showBf ? (n.height! > 0 ? `+${n.height}` : `${n.height}`) : '';

    // Extract clean key from value (strip algorithm-specific suffixes)
    let displayLabel = nodeLabel;
    if (ak === 'treap') displayLabel = nodeLabel.replace(/\(p:[\d.]+\)/, '').trim();
    else if (ak === 'leftist') displayLabel = nodeLabel.replace(/\(d:\d+\)/, '').trim();
    else if (ak === 'binomial') displayLabel = nodeLabel.replace(/\(B\d+\)/, '').trim();
    else if (ak === 'splay') displayLabel = nodeLabel.replace(/\(ROOT\)/, '').trim();
    else if (ak === 'kd') displayLabel = nodeLabel.replace(/\/[xy]$/, '').trim();

    return (
      <g key={`node-${n.id}`} role="img" aria-label={`Node ${displayLabel}, state: ${n.state}`}>
        {/* Red-Black outer ring */}
        {n.rbColor && (
          <circle
            cx={n.x} cy={n.y}
            r={R + 4}
            fill="none"
            stroke={n.rbColor === 'red' ? C.rbRed : C.rbBlack}
            strokeWidth={3}
            opacity={0.75}
          />
        )}

        {/* Violation flash (treap/leftist) */}
        {isViolation && n.state === 'active' && (ak === 'treap' || ak === 'leftist') && (
          <circle cx={n.x} cy={n.y} r={R + 6} fill="none" stroke={C.violationRed} strokeWidth={2} opacity={0.6}>
            {!reducedMotion && (
              <animate attributeName="r" values={`${R + 4};${R + 8};${R + 4}`} dur="0.8s" repeatCount="indefinite" />
            )}
          </circle>
        )}

        {/* Node fill */}
        <circle
          cx={n.x} cy={n.y}
          r={R}
          fill={st.fill}
          stroke={colorBlindMode ? `var(--cb-${n.state === 'default' ? 'default' : n.state})` : st.stroke}
          strokeWidth={n.state === 'active' ? 3 : n.state === 'compare' ? 2 : 1.5}
          filter={n.state === 'active' ? 'url(#tv-glow)' : undefined}
        />

        {/* Color-blind shape overlay */}
        {colorBlindMode && <CbShapeOverlay cx={n.x} cy={n.y} state={n.state} />}

        {/* Value label */}
        <text
          x={n.x} y={n.y}
          fill={st.label}
          fontSize={displayLabel.length > 3 ? '10' : '12'}
          fontWeight="700"
          textAnchor="middle"
          dominantBaseline="middle"
          style={{ fontFamily: 'monospace', userSelect: 'none', pointerEvents: 'none' }}
        >
          {displayLabel.slice(0, 5)}
        </text>

        {/* Red-Black color label inside node */}
        {n.rbColor && !colorBlindMode && (
          <text
            x={n.x} y={n.y + R - 6}
            fill={n.rbColor === 'red' ? C.rbRed : '#94a3b8'}
            fontSize="7" fontWeight="bold"
            textAnchor="middle"
            style={{ userSelect: 'none' }}
          >
            {n.rbColor === 'red' ? 'R' : 'B'}
          </text>
        )}

        {/* AVL balance factor badge */}
        {showBf && (
          <text
            x={n.x + R + 2} y={n.y - R - 2}
            fill={C.avlBadge}
            fontSize="9" fontWeight="bold"
            textAnchor="start" dominantBaseline="middle"
            style={{ userSelect: 'none' }}
          >
            bf={bfLabel}
          </text>
        )}

        {/* Treap priority badge */}
        {ak === 'treap' && n.priority !== undefined && (
          <text
            x={n.x} y={n.y + R + 10}
            fill={C.treapPri}
            fontSize="8" fontWeight="bold"
            textAnchor="middle"
            style={{ userSelect: 'none' }}
          >
            p={n.priority.toFixed(2)}
          </text>
        )}

        {/* Leftist Heap npl badge */}
        {ak === 'leftist' && n.npl !== undefined && (
          <text
            x={n.x + R + 2} y={n.y - R - 2}
            fill={C.leftistNpl}
            fontSize="8" fontWeight="bold"
            textAnchor="start" dominantBaseline="middle"
            style={{ userSelect: 'none' }}
          >
            npl={n.npl}
          </text>
        )}

        {/* Binomial Heap degree badge */}
        {ak === 'binomial' && n.degree !== undefined && (
          <text
            x={n.x + R + 2} y={n.y - R - 2}
            fill={C.binomialDeg}
            fontSize="8" fontWeight="bold"
            textAnchor="start" dominantBaseline="middle"
            style={{ userSelect: 'none' }}
          >
            B{n.degree}
          </text>
        )}

        {/* Cartesian Tree inorder index badge */}
        {ak === 'cartesian' && n.inorderIndex !== undefined && (
          <text
            x={n.x + R + 2} y={n.y - R - 2}
            fill={C.cartesianIdx}
            fontSize="8" fontWeight="bold"
            textAnchor="start" dominantBaseline="middle"
            style={{ userSelect: 'none' }}
          >
            #{n.inorderIndex}
          </text>
        )}

        {/* KD-Tree axis label */}
        {ak === 'kd' && n.axis !== undefined && (
          <text
            x={n.x + R + 2} y={n.y - R - 2}
            fill={C.binomialDeg}
            fontSize="8" fontWeight="bold"
            textAnchor="start" dominantBaseline="middle"
            style={{ userSelect: 'none' }}
          >
            {n.axis === 0 ? 'x-axis' : 'y-axis'}
          </text>
        )}

        {/* Trie end-of-word green diamond marker */}
        {ak === 'trie' && displayLabel.includes('*') && (
          <>
            <rect
              x={n.x - 4} y={n.y - R - 12}
              width={8} height={8}
              transform={`rotate(45 ${n.x} ${n.y - R - 8})`}
              fill={C.trieEnd}
              opacity={0.85}
            />
            <text
              x={n.x + 8} y={n.y - R - 8}
              fill={C.trieEnd}
              fontSize="7" fontWeight="bold"
              textAnchor="start" dominantBaseline="middle"
              style={{ userSelect: 'none' }}
            >
              EOW
            </text>
          </>
        )}

        {/* Binary Lifting depth badge */}
        {ak === 'binary-lift' && (
          <text
            x={n.x} y={n.y - R - 6}
            fill={C.liftLabel}
            fontSize="8" fontWeight="bold"
            textAnchor="middle" dominantBaseline="middle"
            style={{ userSelect: 'none' }}
          >
            {n.state === 'active' ? 'LCA path' : `d=${n.y > 0 ? Math.floor(n.y / LEVEL_H) : 0}`}
          </text>
        )}

        {/* Persistent Segment Tree version badge */}
        {ak === 'persistent-segtree' && (
          <text
            x={n.x} y={n.y - R - 6}
            fill={C.pstVersion}
            fontSize="8" fontWeight="bold"
            textAnchor="middle" dominantBaseline="middle"
            style={{ userSelect: 'none' }}
          >
            {n.state === 'active' ? 'path-copy' : `v${displayLabel}`}
          </text>
        )}

        {/* Rope: leaf nodes render as amber rounded rect with text, internal as indigo circle with len */}
        {ak === 'rope' && (() => {
          const isLeaf = !n.leftChildId && !n.rightChildId && (!n.childrenIds || n.childrenIds.length === 0);
          if (isLeaf) {
            const text = displayLabel;
            const w = Math.max(R * 1.5, text.length * 7 + 12);
            return (
              <>
                <rect
                  x={n.x - w / 2} y={n.y - R * 0.7}
                  width={w} height={R * 1.4}
                  rx={6}
                  fill={n.state === 'active' ? 'rgba(245,158,11,0.25)' : 'rgba(30,20,5,0.9)'}
                  stroke={C.ropeLeaf}
                  strokeWidth={n.state === 'active' ? 2.5 : 1.5}
                  opacity={0.95}
                />
                <text
                  x={n.x} y={n.y}
                  fill={C.ropeLeaf}
                  fontSize="10" fontWeight="600"
                  textAnchor="middle" dominantBaseline="middle"
                  style={{ fontFamily: 'monospace', userSelect: 'none' }}
                >
                  {text.slice(0, 8)}
                </text>
              </>
            );
          }
          return (
            <text
              x={n.x} y={n.y - R - 6}
              fill={C.ropeInternal}
              fontSize="8" fontWeight="bold"
              textAnchor="middle" dominantBaseline="middle"
              style={{ userSelect: 'none' }}
            >
              {n.state === 'active' ? 'split' : `len=${displayLabel}`}
            </text>
          );
        })()}

        {/* Range Tree: coordinate badge above each node */}
        {ak === 'range-tree' && (
          <text
            x={n.x} y={n.y - R - 6}
            fill="#06b6d4"
            fontSize="8" fontWeight="bold"
            textAnchor="middle" dominantBaseline="middle"
            style={{ userSelect: 'none' }}
          >
            {displayLabel}
          </text>
        )}

        {/* Huffman: freq badge on internal nodes, char:freq on leaves */}
        {ak === 'huffman' && (
          <text
            x={n.x} y={n.y - R - 6}
            fill="#f97316"
            fontSize="8" fontWeight="bold"
            textAnchor="middle" dominantBaseline="middle"
            style={{ userSelect: 'none' }}
          >
            {n.state === 'active' ? 'merge' : `f=${displayLabel}`}
          </text>
        )}

        {/* Minimax: MAX/MIN label based on node value pattern */}
        {ak === 'minimax' && (
          <text
            x={n.x} y={n.y - R - 6}
            fill={displayLabel === '?' ? '#64748b' : '#22d3ee'}
            fontSize="8" fontWeight="bold"
            textAnchor="middle" dominantBaseline="middle"
            style={{ userSelect: 'none' }}
          >
            {n.state === 'active' ? 'eval' : displayLabel}
          </text>
        )}

        {/* Splay step label near active node */}
        {ak === 'splay' && n.state === 'active' && splayStep && (
          <text
            x={n.x} y={n.y - R - 10}
            fill={C.splayLabel}
            fontSize="9" fontWeight="bold"
            textAnchor="middle"
            style={{ userSelect: 'none' }}
          >
            {splayStep}
          </text>
        )}

        {/* AVL rotation label near active node */}
        {ak === 'avl' && n.state === 'active' && rotationType && (
          <text
            x={n.x} y={n.y - R - 10}
            fill={C.avlBadge}
            fontSize="9" fontWeight="bold"
            textAnchor="middle"
            style={{ userSelect: 'none' }}
          >
            {rotationType}
          </text>
        )}

        {/* Pairing Heap action label */}
        {ak === 'pairing' && n.state === 'active' && (
          <text
            x={n.x} y={n.y - R - 10}
            fill={C.pairingLabel}
            fontSize="8" fontWeight="bold"
            textAnchor="middle"
            style={{ userSelect: 'none' }}
          >
            {explanation.toLowerCase().includes('extract') ? 'merge-pairs' : 'merge'}
          </text>
        )}

        {/* Heap swap arrow label (array-payload heaps) */}
        {heapAction && n.state === 'active' && ak === 'generic' && (
          <text
            x={n.x} y={n.y - R - 10}
            fill={C.heapSwap}
            fontSize="8" fontWeight="bold"
            textAnchor="middle"
            style={{ userSelect: 'none' }}
          >
            {heapAction}
          </text>
        )}
      </g>
    );
  });

  // ── Focal highlight pass ──────────────────────────────────────────────────

  const highlightElements = placed
    .filter((n) => n.state === 'active')
    .flatMap((n) => [
      // Translucent amber fill halo
      <circle
        key={`hl-fill-${n.id}`}
        cx={n.x} cy={n.y}
        r={R + 9}
        fill={`${C.glow}1a`}
        stroke="none"
      />,
      // Amber glow ring
      <circle
        key={`hl-ring-${n.id}`}
        cx={n.x} cy={n.y}
        r={R + 7}
        fill="none"
        stroke={C.glow}
        strokeWidth={1.5}
        opacity={0.75}
        filter="url(#tv-glow)"
      />,
    ]);

  // ── Rotation arrow overlay (AVL / Splay) ──────────────────────────────────

  const rotActiveNode = placed.find((n) => n.state === 'active');
  const rotationArrows: React.ReactElement[] = [];
  if (rotActiveNode && (rotationType || splayStep)) {
    const parent = placed.find((n) =>
      n.leftChildId === rotActiveNode.id || n.rightChildId === rotActiveNode.id
    );
    if (parent) {
      const ax = rotActiveNode.x, ay = rotActiveNode.y;
      const px = parent.x, py = parent.y;
      const midX = (ax + px) / 2;
      const midY = (ay + py) / 2 - 30;
      const color = rotationType ? C.avlBadge : C.splayLabel;
      const label = rotationType ?? splayStep ?? '';
      rotationArrows.push(
        <path
          key="rot-arrow"
          d={`M ${ax} ${ay - R} Q ${midX} ${midY} ${px} ${py + R}`}
          fill="none"
          stroke={color}
          strokeWidth={2}
          strokeDasharray="4 3"
          opacity={0.7}
          markerEnd="url(#tv-arr-rotation)"
        />,
        <text
          key="rot-label"
          x={midX} y={midY}
          fill={color}
          fontSize="10" fontWeight="bold"
          textAnchor="middle"
          style={{ userSelect: 'none' }}
        >
          {label}
        </text>,
      );
    }
  }

  // ── Legend strip ──────────────────────────────────────────────────────────

  const legendItems: { color: string; label: string }[] = [
    { color: C.strokeDefault, label: 'Default' },
    { color: C.strokeActive,  label: 'Active'  },
    { color: C.strokeVisited, label: 'Visited' },
    { color: C.strokeCompare, label: 'Compare' },
    { color: C.strokeHeap,    label: 'Heap'    },
  ];
  const legendY = H - 20;

  // ── Render ────────────────────────────────────────────────────────────────

  const activeNode = placed.find((n) => n.state === 'active');
  const stepDescription = currentStep?.metadata?.explanation ?? '';

  const overlayText = stepDescription.length > 70
    ? stepDescription.slice(0, 67) + '…'
    : stepDescription;
  const overlayW = Math.min(W - 16, 420);

  return (
    <div ref={containerRef} className="relative w-full h-full overflow-hidden">
      <svg
        width={W}
        height={H}
        viewBox={`0 0 ${W} ${H}`}
        style={{ display: 'block', background: C.bg }}
        role="img"
        aria-label={`Tree visualization. ${activeNode ? `Active node: ${String(activeNode.label ?? activeNode.value)}.` : ''} ${stepDescription}`.trim()}
      >
        <title>{`Tree visualization${activeNode ? ` — active: ${String(activeNode.label ?? activeNode.value)}` : ''}`}</title>
        <defs>
          <filter id="tv-glow" x="-50%" y="-50%" width="200%" height="200%">
            <feGaussianBlur stdDeviation="5" result="blur" />
            <feMerge>
              <feMergeNode in="blur" />
              <feMergeNode in="SourceGraphic" />
            </feMerge>
          </filter>
          {/* Solid filled-polygon arrowheads for directed tree edges */}
          <marker id="tv-arr-default" viewBox="0 0 12 12" refX="11" refY="6"
            markerWidth="6" markerHeight="6" orient="auto-start-reverse">
            <polygon points="0,1 12,6 0,11" fill={C.edgeDefault} />
          </marker>
          <marker id="tv-arr-active" viewBox="0 0 12 12" refX="11" refY="6"
            markerWidth="6" markerHeight="6" orient="auto-start-reverse">
            <polygon points="0,1 12,6 0,11" fill={C.edgeActive} />
          </marker>
          <marker id="tv-arr-path" viewBox="0 0 12 12" refX="11" refY="6"
            markerWidth="6" markerHeight="6" orient="auto-start-reverse">
            <polygon points="0,1 12,6 0,11" fill={C.edgePath} />
          </marker>
          <marker id="tv-arr-compare" viewBox="0 0 12 12" refX="11" refY="6"
            markerWidth="6" markerHeight="6" orient="auto-start-reverse">
            <polygon points="0,1 12,6 0,11" fill={C.edgeCompare} />
          </marker>
          <marker id="tv-arr-rotation" viewBox="0 0 12 12" refX="11" refY="6"
            markerWidth="6" markerHeight="6" orient="auto-start-reverse">
            <polygon points="0,1 12,6 0,11" fill={C.avlBadge} />
          </marker>
        </defs>

        {/* Edges — bottom layer */}
        <g>{edgeElements}</g>

        {/* Nodes — middle layer */}
        <g>{nodeElements}</g>

        {/* Focal glow — top layer */}
        {highlightElements.length > 0 && <g>{highlightElements}</g>}

        {/* Rotation arrows — overlay layer */}
        {rotationArrows.length > 0 && <g>{rotationArrows}</g>}

        {/* Legend strip */}
        <g>
          {legendItems.map((item, i) => {
            const lx = 12 + i * 72;
            return (
              <g key={item.label}>
                <circle cx={lx + 6} cy={legendY} r={5} fill="none" stroke={item.color} strokeWidth={1.5} />
                <text x={lx + 14} y={legendY} fill="#475569" fontSize="8" dominantBaseline="middle" className="select-none">
                  {item.label}
                </text>
              </g>
            );
          })}
        </g>

        {/* Step explanation overlay — top-left */}
        {overlayText && (
          <g>
            <rect
              x={8} y={8}
              width={overlayW}
              height={22}
              rx={6}
              fill="rgba(10,10,14,0.82)"
              stroke="rgba(255,255,255,0.06)"
              strokeWidth={0.5}
            />
            <text
              x={16}
              y={19}
              fill="#a1a1aa"
              fontSize="10"
              fontFamily="monospace"
              dominantBaseline="middle"
              className="select-none"
            >
              {overlayText}
            </text>
          </g>
        )}
      </svg>
    </div>
  );
});

export default TreeVisualizer;
